"use client";

import React from "react";
import { useController, Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Textarea } from "../ui/textarea";
import { cn } from "../../../lib/src/utils";

interface TextareaInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  control: Control<TFieldValues>;
  label: string;
  placeholder?: string;
  description?: string;
  helperText?: string;
  errorText?: string;
  rows?: number;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}

export function TextareaInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label,
  placeholder,
  description,
  helperText,
  errorText,
  rows = 3,
  className,
  required = false,
  disabled = false,
}: TextareaInputProps<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const hasError = error || errorText;

  return (
    <FormItem className={cn("space-y-2", className)}>
      <FormLabel className={cn(required && "after:content-['*'] after:ml-0.5 after:text-red-500")}>
        {label}
      </FormLabel>
      <FormControl>
        <Textarea
          {...field}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          className={cn(
            hasError && "border-destructive focus-visible:ring-destructive",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        />
      </FormControl>
      {description && <FormDescription className="text-sm text-muted-foreground">{description}</FormDescription>}
      {helperText && !hasError && <FormDescription className="text-sm text-muted-foreground">{helperText}</FormDescription>}
      {hasError && <FormMessage>{errorText || error?.message}</FormMessage>}
    </FormItem>
  );
}
